import React, { useState } from "react";
import {
  Card,
  Typography,
  Modal,
  Button,
  Tooltip,
  Upload,
  message,
  Form,
  Input
} from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { db } from "../firebaseConfig";
import { collection, addDoc } from "firebase/firestore";

const { Title, Paragraph } = Typography;

const automationData = [
  {
    id: 1,
    title: "eBiz Timesheet Automation",
    description:
      "Automates task defaulting in eBiz using Selenium and fetches Jira tasks.",
    fileUrl: "https://huggingface.co/datasets/AkhilYadav/MyAutomations/resolve/main/EbizTimesheetBot.zip?download=true",
    prerequisites: [
      "Install Python and Selenium library",
      "ChromeDriver must be in system PATH.",
      "Jira and eBiz credentials required."
    ],
    usageSteps: [
      "Enter your credentials directly into the Python script",
      "Save the script and execute it",
      "The script will automatically default the task and corresponding hours in eBiz",
      "Review the defaulted entries to ensure accuracy, then proceed to save and submit your timesheet",
      "Ensure you're running the script in the designated driver environment, not in your local browser"
    ]
  },
  {
    id: 2,
    title: "SEAT script execution automation",
    description:
      "Automatically clears previous results and executes all scripts for the specified SSAs—no manual intervention required",
    fileUrl: "https://huggingface.co/datasets/AkhilYadav/MyAutomations/resolve/main/RUN.SST.b.zip?download=true",
    prerequisites: [
      "Make sure you are having TAFJ runtime environment",
      "Make sure you are running the script under tafj project which is linked to the DB"
    ],
    usageSteps: [
        "Create a `.txt` file containing the list of SSA IDs you want to run, with comma seperated.",
        "Open the `RUN.SST` program and update the `IN.FILE.DIR` and `IN.FILE.NAME` fields with the appropriate file path and name.",
        "Compile the `RUN.SST` program and execute it to run the scripts for the listed SSAs.",
        "Ensure the `.txt` file is not open in any other program while running `RUN.SST`.",
        "Avoid including any spaces when entering the SSA IDs in the file."
    ]
  },
  {
    id: 3,
    title: "Extract dev delivery details",
    description:
      "Extracts the task and artifact details that are delivered for current run for mentioned project",
    fileUrl: "https://huggingface.co/datasets/AkhilYadav/MyAutomations/resolve/main/tool-bitbucket.zip?download=true",
    prerequisites: [
      "requires a python 3 environment to run this tool",
      "BitBucket > installation > Install-Requirements.bat (trigger to install all dependencies on its own)"
    ],
    usageSteps: [
        "Run bitbucket-Retail.bat",
        "Enter project and repositories you want to extract",
        "Select first tag for dev deliveries",
        "Once execution is completed you can see results Run_Details folder"
    ]
  },
  {
    id: 4,
    title: "Find record in PDC (Program data control)",
    description:
      "Checks PDC record for the mentioned record id and stores module and component details if record is found",
    fileUrl: "https://huggingface.co/datasets/AkhilYadav/MyAutomations/resolve/main/FIND.RECORDS.IN.PDC.b.zip?download=true",
    prerequisites: [
      "Make sure you are having TAFJ runtime environment",
      "Make sure you are running the script under tafj project which is linked to the DB"
    ],
    usageSteps: [
        "Create a text file with record IDs, separated by commas.",
        "Update the program to include the correct file name and path.",
        "Compile and execute the program.",
        "An Excel file named 'results' will be generated at the specified location.",
        "If a record exists in PDC, the corresponding component and module names will be listed."
    ]
  },
  {
    id: 5,
    title: "Upfront Profit Calculator",
    description:
      "Excel-based dynamic amortization and profit calculation tool.",
    fileUrl: "https://huggingface.co/datasets/AkhilYadav/MyAutomations/resolve/main/UpfrontProfitCalculator.zip?download=true",
    prerequisites: [
      "Microsoft Excel or compatible software installed.",
      "Basic understanding of loan amortization."
    ],
    usageSteps: [
      "Enter principal, interest, term, etc.",
      "Review amortization and profit calculations.",
      "Download or share the Excel report."
    ]
  }
];

const Automations = () => {
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedAutomation, setSelectedAutomation] = useState(null);
  const [form] = Form.useForm();

  const showModal = (automation) => {
    setSelectedAutomation(automation);
    setModalVisible(true);
  };

  const closeModal = () => {
    setModalVisible(false);
    setSelectedAutomation(null);
  };

  const onFinish = async (values) => {
    try {
      await addDoc(collection(db, "pending_automations"), {
        ...values,
        prerequisites: values.prerequisites?.split(",").map(p => p.trim()),
        usageSteps: values.usageSteps?.split(",").map(u => u.trim()),
        isExternal: true, // Backend use only
        published: false,
        createdAt: new Date()
      });
      message.success("Your automation was submitted successfully!");
      form.resetFields();
    } catch (error) {
      console.error("Error saving document: ", error);
      message.error("Something went wrong!");
    }
  };

  return (
    <Card
      style={{
        maxWidth: 1000,
        margin: "40px auto",
        padding: 30,
        borderRadius: "16px",
        boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
        background: "#fdfdfd"
      }}
    >
      <Title level={2} style={{ textAlign: "center", marginBottom: 40 }}>
        My Automations
      </Title>

      {automationData.map((automation) => (
        <Card
          key={automation.id}
          hoverable
          onClick={() => showModal(automation)}
          style={{
            marginBottom: 20,
            borderRadius: 12,
            boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
            border: "1px solid #f0f0f0"
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}
          >
            <div>
              <Title level={4} style={{ marginBottom: 4 }}>
                {automation.title}
              </Title>
              <Paragraph style={{ marginBottom: 0 }}>
                {automation.description}
              </Paragraph>
              {automation.linkedinUrl && (
                <Paragraph style={{ margin: "8px 0 0" }}>
                  Contributed by:{" "}
                  <a href={automation.linkedinUrl} target="_blank" rel="noreferrer">
                    LinkedIn Profile
                  </a>
                </Paragraph>
              )}
            </div>
            <Tooltip title="Download">
              <Button
                type="primary"
                shape="circle"
                icon={<DownloadOutlined />}
                href={automation.fileUrl}
                download
                onClick={(e) => {
                  e.stopPropagation();
                  const link = document.createElement("a");
                  link.href = automation.fileUrl;
                  link.setAttribute("download", ""); // leave filename empty for default
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
              />
            </Tooltip>
          </div>
        </Card>
      ))}

      <Modal
        title={selectedAutomation?.title}
        open={modalVisible}
        onCancel={closeModal}
        footer={null}
        centered
      >
        {selectedAutomation?.prerequisites?.length > 0 && (
          <>
            <Title level={5}>Prerequisites:</Title>
            <ul style={{ paddingLeft: 20 }}>
              {selectedAutomation.prerequisites.map((step, index) => (
                <li key={index} style={{ marginBottom: 8 }}>{step}</li>
              ))}
            </ul>
          </>
        )}

        {selectedAutomation?.usageSteps?.length > 0 && (
          <>
            <Title level={5}>Usage Steps:</Title>
            <ul style={{ paddingLeft: 20 }}>
              {selectedAutomation.usageSteps.map((step, index) => (
                <li key={index} style={{ marginBottom: 8 }}>{step}</li>
              ))}
            </ul>
          </>
        )}
      </Modal>

      <Card
        style={{
          marginTop: 40,
          padding: 20,
          background: "#fff7e6",
          border: "1px dashed #faad14"
        }}
      >
        <Title level={4}>Contribute Your Automation</Title>
        <Paragraph>
          Upload your automation here. We will review and publish it after approval.
        </Paragraph>

        <Form
          layout="vertical"
          form={form}
          onFinish={onFinish}
          style={{ marginTop: 24 }}
        >
          <Form.Item
            label="Automation Title"
            name="title"
            rules={[{ required: true, message: 'Please enter a title' }]}
          >
            <Input placeholder="Enter the title of your automation" />
          </Form.Item>

          <Form.Item
            label="Short Description"
            name="description"
            rules={[{ required: true, message: 'Please enter a description' }]}
          >
            <Input.TextArea rows={3} placeholder="Describe what your automation does" />
          </Form.Item>

          <Form.Item label="Prerequisites" name="prerequisites">
            <Input.TextArea
              rows={2}
              placeholder="List any prerequisites (comma separated)"
            />
          </Form.Item>

          <Form.Item label="Usage Steps" name="usageSteps">
            <Input.TextArea
              rows={3}
              placeholder="Provide usage steps (comma separated)"
            />
          </Form.Item>

          <Form.Item
            label="Download URL (Google Drive or GitHub public link)"
            name="fileUrl"
            rules={[{ required: true, message: 'Please enter a valid file URL' }]}
          >
            <Input placeholder="Enter public download URL of your automation" />
          </Form.Item>

          <Form.Item label="LinkedIn URL (optional)" name="linkedinUrl">
            <Input placeholder="https://www.linkedin.com/in/yourprofile" />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit">
              Submit for Review
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </Card>
  );
};

export default Automations;
